// client/src/hooks/useCookieConsent.ts
import { useCookieConsent as useCookieConsentContext } from '../contexts/CookieConsentContext';
import type { CookieConsentState } from '../contexts/CookieConsentContext';

// Enhanced hook with additional utility functions
export function useCookieConsent() {
  const context = useCookieConsentContext();

  // Check if analytics consent is given
  const hasAnalyticsConsent = context.hasGivenConsent('analytics');

  // Check if marketing consent is given
  const hasMarketingConsent = context.hasGivenConsent('marketing');

  // Check if essential consent is given (always true)
  const hasEssentialConsent = context.hasGivenConsent('essential');

  // Get consent status as a readable object
  const consentStatus: CookieConsentState = {
    essential: hasEssentialConsent,
    analytics: hasAnalyticsConsent,
    marketing: hasMarketingConsent,
  };

  // Check if user has made any consent decision
  const hasDecided = context.hasConsent;

  // Check if all cookies are accepted
  const allAccepted = hasEssentialConsent && hasAnalyticsConsent && hasMarketingConsent;

  // Check if only essential cookies are accepted
  const essentialOnly = hasEssentialConsent && !hasAnalyticsConsent && !hasMarketingConsent;

  // Custom consent update with validation
  const updateConsent = (category: keyof CookieConsentState, value: boolean) => {
    // Essential cookies cannot be disabled
    if (category === 'essential' && !value) {
      console.warn('Essential cookies cannot be disabled');
      return;
    }
    
    context.updateConsent(category, value);
  };

  // Get consent summary for display
  const getConsentSummary = () => {
    const categories = [];
    
    if (hasEssentialConsent) categories.push('Essential');
    if (hasAnalyticsConsent) categories.push('Analytics');
    if (hasMarketingConsent) categories.push('Marketing');
    
    return categories.join(', ');
  };

  // Check if consent needs to be refreshed (e.g., for policy updates)
  const needsRefresh = () => {
    // This could be expanded to check for policy version changes
    return false;
  };

  return {
    ...context,
    hasAnalyticsConsent,
    hasMarketingConsent,
    hasEssentialConsent,
    consentStatus,
    hasDecided,
    allAccepted,
    essentialOnly,
    updateConsent,
    getConsentSummary,
    needsRefresh,
  };
}

export default useCookieConsent;
